// components/PlacementVendorDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreatePlacementVendorMutation,
  useUpdatePlacementVendorMutation,
} from "@/hooks/api/placementVendor.queries";
import { departmentService } from "@/services/department.service";
import {
  PlacementVendorCreateType,
  PlacementVendorParamsType,
  PlacementVendorType,
  fetchIndustryOptions,
} from "@/services/placementVendor.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type PlacementVendorDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: PlacementVendorParamsType;
  vendorId?: string;
  vendorData?: PlacementVendorType | null;
  onCancel: () => void;
};

const placementVendorSchema = z.object({
  companyName: z.string().min(1, "Company Name is required"),
  contactPerson: z.string().min(1, "Contact Person is required"),
  email: z.string().email("Invalid email").min(1, "Email is required"),
  phone: z.string().min(1, "Phone is required"),
  website: z.string().optional(),
  status: z.enum(["active", "inactive", "pending"]),
  industry: z.array(z.string()).min(1, "Select at least one industry"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  preferredDepartments: z.array(z.string()).optional(),
  salaryPackage: z.string().min(1, "Salary Package is required"),
  availablePositions: z.number().min(0).optional(),
  companyDescription: z.string().optional(),
  eligibilityCriteria: z.string().optional(),
  jobRequirements: z.string().optional(),
});

type PlacementVendorFormType = z.infer<typeof placementVendorSchema>;

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Pending", value: "pending" },
];

const PlacementVendorDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  vendorId,
  vendorData,
  onCancel,
}: PlacementVendorDialogProps) => {
  const createMutation = useCreatePlacementVendorMutation(filters);
  const updateMutation = useUpdatePlacementVendorMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<PlacementVendorFormType>({
    resolver: zodResolver(placementVendorSchema),
    defaultValues: {
      companyName: "",
      contactPerson: "",
      email: "",
      phone: "",
      website: "",
      status: "active",
      industry: [],
      city: "",
      state: "",
      preferredDepartments: [],
      salaryPackage: "",
      availablePositions: 0,
      companyDescription: "",
      eligibilityCriteria: "",
      jobRequirements: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && vendorData) {
        setTimeout(() => {
          form.reset({
            companyName: vendorData.companyName,
            contactPerson: vendorData.contactPerson,
            email: vendorData.email,
            phone: vendorData.phone,
            website: vendorData.website || "",
            status: vendorData.status,
            industry: vendorData.industry || [],
            city: vendorData.city || "",
            state: vendorData.state || "",
            preferredDepartments: vendorData.preferredDepartments || [],
            salaryPackage: vendorData.salaryPackage || "",
            availablePositions: vendorData.availablePositions || 0,
            companyDescription: vendorData.companyDescription || "",
            eligibilityCriteria: vendorData.eligibilityCriteria || "",
            jobRequirements: vendorData.jobRequirements || "",
          });
        }, 0);
      } else {
        form.reset({
          companyName: "",
          contactPerson: "",
          email: "",
          phone: "",
          website: "",
          status: "active",
          industry: [],
          city: "",
          state: "",
          preferredDepartments: [],
          salaryPackage: "",
          availablePositions: 0,
          companyDescription: "",
          eligibilityCriteria: "",
          jobRequirements: "",
        });
      }
    }
  }, [mode, vendorData, isOpen, form]);

  const handleSubmit = (values: PlacementVendorFormType) => {
    const submissionData: PlacementVendorCreateType = {
      ...values,
      website: values.website || "",
      industry: values.industry || [],
      city: values.city || "",
      state: values.state || "",
      preferredDepartments: values.preferredDepartments || [],
      salaryPackage: values.salaryPackage || "",
      availablePositions: values.availablePositions || 0,
      companyDescription: values.companyDescription || "",
      eligibilityCriteria: values.eligibilityCriteria || "",
      jobRequirements: values.jobRequirements || "",
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && vendorId) {
      updateMutation.mutate(
        { _id: vendorId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  const defaultIndustryOptions =
    vendorData?.industry?.map((i) => ({
      label: i,
      value: i,
    })) || [];

  const defaultDepartmentOptions =
    vendorData?.preferredDepartments?.map((id) => ({
      label: `Dept ${id}`, // Placeholder as we don't have department names directly
      value: id,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Add New Placement Vendor"
              : "Edit Placement Vendor"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Company Name */}
            <InputField<PlacementVendorFormType>
              type="text"
              name="companyName"
              label="Company Name"
              placeholder="e.g. Google Inc."
              required
              disabled={isPending}
            />

            {/* Contact Person */}
            <InputField<PlacementVendorFormType>
              type="text"
              name="contactPerson"
              label="Contact Person"
              placeholder="e.g. Emily Chen"
              required
              disabled={isPending}
            />

            {/* Email */}
            <InputField<PlacementVendorFormType>
              type="email"
              name="email"
              label="Email"
              placeholder="e.g. emily@google.com"
              required
              disabled={isPending}
            />

            {/* Phone */}
            <InputField<PlacementVendorFormType>
              type="tel"
              name="phone"
              label="Phone Number"
              placeholder="e.g. 9876543210"
              inputMode="numeric"
              maxLength={10}
              required
              disabled={isPending}
            />

            {/* Website */}
            <InputField<PlacementVendorFormType>
              type="url"
              name="website"
              label="Website"
              placeholder="https://example.com"
              disabled={isPending}
            />

            {/* Status */}
            <SelectField<PlacementVendorFormType>
              name="status"
              label="Status"
              options={STATUS_OPTIONS}
              required
              disabled={isPending}
            />

            {/* Industry - Multi Select */}
            <AsyncMultiSelectField<PlacementVendorFormType>
              name="industry"
              label="Industry"
              placeholder="Select industries"
              fetchOptions={fetchIndustryOptions}
              queryKey={["industries"]}
              defaultOptions={defaultIndustryOptions}
              required
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* City */}
            <InputField<PlacementVendorFormType>
              type="text"
              name="city"
              label="City"
              placeholder="e.g. Bangalore"
              required
              disabled={isPending}
            />

            {/* State */}
            <InputField<PlacementVendorFormType>
              type="text"
              name="state"
              label="State"
              placeholder="e.g. Karnataka"
              required
              disabled={isPending}
            />

            {/* Preferred Departments - Multi Select */}
            <AsyncMultiSelectField<PlacementVendorFormType>
              name="preferredDepartments"
              label="Preferred Departments"
              placeholder="Select preferred departments"
              fetchOptions={departmentService.fetchDepartmentOptions}
              queryKey={["departments"]}
              defaultOptions={defaultDepartmentOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Salary Package */}
            <InputField<PlacementVendorFormType>
              type="text"
              name="salaryPackage"
              label="Salary Package (LPA)"
              placeholder="e.g. 6-8"
              required
              disabled={isPending}
            />

            {/* Available Positions */}
            <InputField<PlacementVendorFormType>
              type="number"
              name="availablePositions"
              label="Available Positions"
              placeholder="e.g. 10"
              disabled={isPending}
            />

            {/* Company Description */}
            <TextareaField<PlacementVendorFormType>
              name="companyDescription"
              label="Company Description"
              placeholder="Brief description about the company"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Eligibility Criteria */}
            <TextareaField<PlacementVendorFormType>
              name="eligibilityCriteria"
              label="Eligibility Criteria"
              placeholder="e.g. Minimum 70% marks, No active backlogs"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Job Requirements */}
            <TextareaField<PlacementVendorFormType>
              name="jobRequirements"
              label="Job Requirements"
              placeholder="Required skills, qualifications, experience"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Vendor"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PlacementVendorDialog;
